import ActivateEmail from './component/ActivateEmail/ActivateEmail';
import ForgotCustomerID from './component/ForgotCustomerID/ForgotCustomerID';
import HelpFAQ from './component/helpFaq/helpFaq';
import Maintenance from './component/maintenance/Maintenance.js';
import AppRoutes from './component/routes/AppRoutes';
import SecurityVerification from './component/SecurityVerification/SecurityVerification';
import i18n from './i18n.js';
import NetworkChecker from './lib/NetworkChecker/NetworkChecker.js';
import oktaConfig from './okta-config';
import {postAPICall} from './services/apiCallsHandler';
import {MAINTENANCE_UNLEASH_KEY, wrapperCountryMapping} from './utils/constant';
import Smartlook from './utils/smartlook';

import {ToastProvider} from '@deere/fuel-react';
import {OktaAuth, toRelativeUrl} from '@okta/okta-auth-js';
import {Security} from '@okta/okta-react';
import {
  useFlag,
  useUnleashClient,
  useVariant,
} from '@unleash/proxy-client-react';
import React from 'react';
import Helmet from 'react-helmet';
import {Route, Routes, useNavigate} from 'react-router-dom';

import log from 'loglevel';
import './App.css';
import './i18n.js';

const newRelicScript = `/js/new_relic_${import.meta.env.VITE_ENVIRONMENT}.js`;

export const oktaAuth = new OktaAuth({
  issuer: oktaConfig.issuer,
  clientId: oktaConfig.clientId,
  redirectUri: oktaConfig.redirectUri,
});

// React Native WebView delivers native payloads by running script inside the page,
// so those events carry an empty/own origin and no source window.
const isTrustedWrapperMessage = (event) => {
  const allowedOrigin = globalThis.location.origin;
  const originAllowed =
    event.origin === allowedOrigin ||
    event.origin === '' ||
    event.origin === undefined;

  return originAllowed && (!event.source || event.source === globalThis);
};

const putDeviceTokenAPI = async (deviceToken, deleteToken = false) => {
  const checkAuth = await oktaAuth.isAuthenticated();
  if (checkAuth) {
    const {preferred_username} = await oktaAuth.getUser();
    await postAPICall(
      'deviceToken?oktaUserId=' + preferred_username,
      {
        deviceToken: deviceToken,
        deleteToken: deleteToken,
      },
      {
        errorTag: 'SET_DEVICE_TOKEN',
        logInfoMsg: 'customer device token set successfully ',
      }
    );
  }
};

function App() {
  const isProdDown = useFlag(MAINTENANCE_UNLEASH_KEY);
  const maintainanceVariant = useVariant(MAINTENANCE_UNLEASH_KEY);
  const client = useUnleashClient();
  const prodDownTimeValue =
    maintainanceVariant?.payload?.value?.toLowerCase() || '';
  const [isOffline, setIsOffline] = React.useState(false);
  const navigate = useNavigate();
  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    navigate(toRelativeUrl(originalUri || '/', globalThis.location.origin));
  };

  const getFcmToken = (data) => {
    if (!isTrustedWrapperMessage(data)) {
      return;
    }

    if (data.data) {
      try {
        let tempData;
        if (typeof data.data === 'string') {
          tempData = JSON.parse(data.data);
        } else {
          tempData = data.data;
        }

        if (tempData?.reactivatedFcmToken) {
          putDeviceTokenAPI(tempData.reactivatedFcmToken);
          return;
        }

        if (tempData?.deleteUserToken) {
          putDeviceTokenAPI(tempData.token, true);
          return;
        }

        const tempToken = tempData.fcmtoken;
        if (
          typeof tempToken === 'string' &&
          tempToken &&
          !localStorage.getItem('fcmtoken')
        ) {
          localStorage.setItem('fcmtoken', tempToken);
        }
      } catch (err) {
        log.error('Error parsing FCM token message:', err);
      }
    }
  };

  const getWrapperCountry = (data) => {
    if (!isTrustedWrapperMessage(data)) {
      return;
    }

    if (data.data) {
      try {
        let tempData;

        if (typeof data.data === 'string') {
          tempData = JSON.parse(data.data);
        } else {
          tempData = data.data;
        }

        const countryCode = tempData.appCountryCode;
        const mappedCountry = wrapperCountryMapping[countryCode];
        if (mappedCountry) {
          localStorage.setItem('countryCode', mappedCountry);
          i18n.changeLanguage(mappedCountry);
          client.updateContext({country: mappedCountry});
        }
      } catch (err) {
        log.error('Error parsing wrapper country message:', err);
      }
    }
  };

  React.useEffect(() => {
    window.addEventListener('message', getFcmToken);
    document.addEventListener('message', getFcmToken);
    window.addEventListener('message', getWrapperCountry);
    document.addEventListener('message', getWrapperCountry);

    return () => {
      window.removeEventListener('message', getFcmToken);
      document.removeEventListener('message', getFcmToken);
      window.removeEventListener('message', getWrapperCountry);
      document.removeEventListener('message', getWrapperCountry);
    };
  }, [getFcmToken, getWrapperCountry]);

  const setNetworkStatus = (offline) => {
    setIsOffline(offline);
  };
  return (
    <div className={isOffline ? 'App offlineView' : 'App'}>
      <Smartlook />
      <Helmet>
        <script type="text/javascript" src={newRelicScript}></script>
      </Helmet>
      {isProdDown ? (
        <Maintenance data_time={prodDownTimeValue} />
      ) : (
        <>
          <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
            <AppRoutes oktaAuth={oktaAuth} />
          </Security>
          <Routes>
            <Route
              path="/verification"
              element={<SecurityVerification unsecure={true} />}
            />
            <Route
              path="/get-customer-id"
              element={
                <ToastProvider autoHideDuration={20000}>
                  <ForgotCustomerID />
                </ToastProvider>
              }
            />
            <Route path="/faq" element={<HelpFAQ />} />
            <Route path="/activate-email" element={<ActivateEmail />} />
          </Routes>
        </>
      )}
      <NetworkChecker setNetworkStatus={setNetworkStatus} />
    </div>
  );
}

export default App;
